// frontend/src/features/equipment/EquipmentSetupWizard/components/SubcategorySection.tsx
import { useState, useEffect, useRef } from 'react';
import {
  Card,
  Group,
  Text,
  Radio,
  Collapse,
  Stack,
  NumberInput,
  UnstyledButton,
  ActionIcon,
  Box,
} from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconPlus, IconTrash } from '@tabler/icons-react';
import type { Subcategory } from '@/types/equipmentCatalog';
import type { PendingEquipment, HasEquipmentAnswer, BrandModelValue } from '../types';
import { BrandModelInput } from './BrandModelInput';
import { QuantityPrompt } from './QuantityPrompt';

interface SubcategorySectionProps {
  subcategory: Subcategory;
  equipment: PendingEquipment[];
  onEquipmentChange: (equipment: PendingEquipment[]) => void;
}

function generateTempId(): string {
  return `temp-${crypto.randomUUID()}`;
}

function createEmptyEquipment(subcategoryId: string): PendingEquipment {
  return {
    tempId: generateTempId(),
    subcategoryId,
    brandModel: {},
    quantity: 1,
    trackSeparately: false,
  };
}

function getModelDisplayName(brandModel: BrandModelValue): string {
  if (brandModel.modelName) {
    return brandModel.modelName;
  }
  // In real implementation, we'd look up the model name from the catalog
  // For now, return the modelId or empty string
  return brandModel.modelId || '';
}

export function SubcategorySection({
  subcategory,
  equipment,
  onEquipmentChange,
}: SubcategorySectionProps) {
  const hasEquipment = equipment.length > 0;

  // Determine initial answer based on equipment
  const [answer, setAnswer] = useState<HasEquipmentAnswer | null>(
    hasEquipment ? 'yes' : null
  );
  const [isExpanded, setIsExpanded] = useState(hasEquipment);
  const prevEquipmentLengthRef = useRef(equipment.length);

  // Keep expanded state in sync when equipment is added externally
  useEffect(() => {
    const prevLength = prevEquipmentLengthRef.current;
    prevEquipmentLengthRef.current = equipment.length;

    // Only auto-expand when equipment goes from 0 to > 0
    if (prevLength === 0 && equipment.length > 0) {
      setIsExpanded(true);
      setAnswer('yes');
    }
  }, [equipment.length]);

  const handleAnswerChange = (value: string) => {
    const newAnswer = value as HasEquipmentAnswer;
    setAnswer(newAnswer);

    if (newAnswer === 'yes') {
      setIsExpanded(true);
      // Create first equipment entry if none exists
      if (equipment.length === 0) {
        onEquipmentChange([createEmptyEquipment(subcategory.id)]);
      }
    } else {
      setIsExpanded(false);
      // Clear equipment when No or Not yet is selected
      if (equipment.length > 0) {
        onEquipmentChange([]);
      }
    }
  };

  const handleBrandModelChange = (index: number, value: BrandModelValue) => {
    const updated = [...equipment];
    updated[index] = {
      ...updated[index]!,
      brandModel: value,
    };
    onEquipmentChange(updated);
  };

  const handleQuantityChange = (index: number, value: number | string) => {
    const updated = [...equipment];
    updated[index] = {
      ...updated[index]!,
      quantity: typeof value === 'number' ? value : 1,
    };
    onEquipmentChange(updated);
  };

  const handleTrackSeparatelyChange = (index: number, value: boolean) => {
    const updated = [...equipment];
    updated[index] = {
      ...updated[index]!,
      trackSeparately: value,
    };
    onEquipmentChange(updated);
  };

  const handleAddAnother = () => {
    onEquipmentChange([...equipment, createEmptyEquipment(subcategory.id)]);
  };

  const handleRemove = (index: number) => {
    const updated = equipment.filter((_, i) => i !== index);
    if (updated.length === 0) {
      setAnswer(null);
      setIsExpanded(false);
    }
    onEquipmentChange(updated);
  };

  const toggleExpanded = () => {
    if (answer === 'yes') {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <Card withBorder padding="md">
      <Group justify="space-between" mb="sm">
        <Group gap="xs">
          <Text fw={500}>{subcategory.name}</Text>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={toggleExpanded}
            data-testid="chevron-icon"
            disabled={answer !== 'yes'}
          >
            {isExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
          </ActionIcon>
        </Group>
      </Group>

      <Text size="sm" c="dimmed" mb="sm">
        Do you have a {subcategory.name}?
      </Text>

      <Radio.Group value={answer || ''} onChange={handleAnswerChange}>
        <Group gap="lg">
          <Radio value="yes" label="Yes" />
          <Radio value="no" label="No" />
          <Radio value="not_yet" label="Not yet" />
        </Group>
      </Radio.Group>

      <Collapse in={isExpanded && answer === 'yes'}>
        <Stack gap="md" mt="md">
          {equipment.map((item, index) => (
            <Card key={item.tempId} withBorder padding="sm" bg="gray.0">
              <Stack gap="sm">
                {equipment.length > 1 && (
                  <Group justify="space-between">
                    <Text size="sm" fw={500}>
                      {subcategory.name} #{index + 1}
                    </Text>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => handleRemove(index)}
                      aria-label={`Remove ${subcategory.name} ${index + 1}`}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                )}

                <BrandModelInput
                  subcategoryId={subcategory.id}
                  value={item.brandModel}
                  onChange={(value) => handleBrandModelChange(index, value)}
                />

                {subcategory.allowMultiple && (
                  <NumberInput
                    label="Quantity"
                    value={item.quantity}
                    onChange={(value) => handleQuantityChange(index, value)}
                    min={1}
                    max={20}
                    w={100}
                  />
                )}

                {subcategory.allowMultiple && item.quantity > 1 && (
                  <QuantityPrompt
                    modelName={getModelDisplayName(item.brandModel)}
                    quantity={item.quantity}
                    trackSeparately={item.trackSeparately}
                    onTrackSeparatelyChange={(value) =>
                      handleTrackSeparatelyChange(index, value)
                    }
                  />
                )}
              </Stack>
            </Card>
          ))}

          {subcategory.allowMultiple && (
            <Box>
              <UnstyledButton onClick={handleAddAnother}>
                <Group gap="xs">
                  <IconPlus size={16} />
                  <Text size="sm" c="blue">
                    Add different {subcategory.name}
                  </Text>
                </Group>
              </UnstyledButton>
            </Box>
          )}
        </Stack>
      </Collapse>
    </Card>
  );
}
